import Color from 'color'

import type { ThemeMode } from '#web-oura-tools/types/theme'

/** OS/browser preferred color scheme, defaulting to light. */
export function getBrowserThemeMode(): ThemeMode {
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light'
}

// APCA perceptual contrast, ported from Vuetify (https://github.com/Myndex/SAPC-APCA)
function apcaContrast(text: string, background: string): number {
  const luminance = (hex: string) => {
    const [r, g, b] = Color(hex)
      .rgb()
      .array()
      .map((v) => (v / 255) ** 2.4)

    const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175

    return y <= 0.03 ? y + (0.03 - y) ** 1.45 : y
  }

  const yTxt = luminance(text)
  const yBg = luminance(background)

  if (Math.abs(yBg - yTxt) < 0.0005) return 0

  if (yBg > yTxt) {
    const sapc = (yBg ** 0.55 - yTxt ** 0.58) * 1.25

    return (sapc < 0.001 ? 0 : sapc < 0.078 ? sapc - sapc * 12.82051282051282 * 0.06 : sapc - 0.06) * 100
  }

  const sapc = (yBg ** 0.62 - yTxt ** 0.57) * 1.25

  return (sapc > -0.001 ? 0 : sapc > -0.078 ? sapc - sapc * 12.82051282051282 * 0.06 : sapc + 0.06) * 100
}

/** Readable text color (white or black) on the given background, picked the same way as Vuetify's `on-*` colors. */
export function getForegroundColor(background: string): string {
  const blackContrast = Math.abs(apcaContrast('#000000', background))
  const whiteContrast = Math.abs(apcaContrast('#FFFFFF', background))

  return whiteContrast > Math.min(blackContrast, 50) ? '#FFFFFF' : '#000000'
}
